import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  FiSettings,
  FiUsers,
  FiShield,
  FiDatabase,
  FiActivity,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiAlertTriangle,
  FiInfo,
  FiSave,
  FiUser,
  FiLink,
  FiMail,
  FiEye,
  FiEyeOff,
  FiKey
} from 'react-icons/fi';
import { usersAPI, nftsAPI, settingsAPI, secureSettingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import AvatarUpload from '../components/AvatarUpload';
import StableInput from '../components/StableInput';
import BlockchainSettingsTab from '../components/BlockchainSettingsTab';

// Minimal test component to isolate focus issue
const MinimalTestInput = () => {
  const [value, setValue] = React.useState('');
  console.log('MinimalTestInput rendering');
  
  return (
    <div>
      <label className="label">Minimal Test Input</label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          console.log('MinimalTestInput onChange:', e.target.value);
          setValue(e.target.value);
        }}
        className="input"
        placeholder="Test typing here"
        autoComplete="off"
      />
    </div>
  );
};

// Profile Form Component - Using EditChallengeModal pattern from Map.js
const ProfileForm = React.forwardRef(({ user, t }, ref) => {
  const [formData, setFormData] = React.useState({
    fullName: '',
    email: '',
    bio: ''
  });

  // Initialize form data once when user prop changes (same pattern as EditChallengeModal)
  React.useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        email: user.email || '',
        bio: user.bio || ''
      });
    }
  }, [user]);

  // Expose form data to parent component
  React.useImperativeHandle(ref, () => ({
    getFormData: () => formData
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="label">{t('cms.settings.fullName')}</label>
        <input
          type="text"
          value={formData.fullName}
          onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
          className="input"
          placeholder="Enter your full name"
          autoComplete="off"
        />
      </div>

      <div>
        <label className="label">{t('cms.settings.email')}</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          className="input"
          placeholder="admin@example.com"
          autoComplete="off"
        />
      </div>

      <div className="md:col-span-2">
        <label className="label">Bio</label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
          className="input min-h-[100px] resize-vertical"
          placeholder="Tell us a bit about yourself..."
          maxLength="500"
        />
        <p className="text-xs text-gray-500 mt-1">
          {formData.bio.length}/500 characters
        </p>
      </div>
    </div>
  );
});

// Create a working profile input based on the MinimalTestInput pattern
const ProfileFullNameInput = ({ initialValue, onValueChange }) => {
  const [value, setValue] = React.useState(initialValue || '');
  console.log('ProfileFullNameInput rendering with value:', value);
  
  // Sync with parent when initialValue changes (only once during initialization)
  React.useEffect(() => {
    if (initialValue && !value) {
      setValue(initialValue);
    }
  }, [initialValue, value]);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    console.log('ProfileFullNameInput onChange:', newValue);
    setValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };
  
  return (
    <div>
      <label className="label">Full Name (Working)</label>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        className="input"
        placeholder="Enter your full name"
        autoComplete="off"
      />
    </div>
  );
};

// Uncontrolled input that maintains focus
const UncontrolledInputField = ({ label, defaultValue, onChange, type = 'text', placeholder, help }) => {
  const inputRef = React.useRef(null);
  
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };
  
  // Update default value only when component first mounts
  React.useEffect(() => {
    if (inputRef.current && defaultValue && !inputRef.current.value) {
      inputRef.current.value = defaultValue;
    }
  }, [defaultValue]);
  
  return (
    <div>
      <label className="label">{label}</label>
      <input
        ref={inputRef}
        type={type}
        defaultValue={defaultValue || ''}
        onChange={handleChange}
        className="input"
        placeholder={placeholder}
        autoComplete="off"
      />
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );
};

// Stable input field component to prevent focus loss
const InputField = React.memo(({ label, value, onChange, type = 'text', placeholder, help }) => {
  const inputRef = React.useRef(null);
  const [internalValue, setInternalValue] = React.useState(value || '');
  
  // Sync internal value with prop only on mount or when prop changes significantly
  React.useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);
  
  const handleChange = React.useCallback((e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange(newValue);
  }, [onChange]);
  
  return (
    <div>
      <label className="label">{label}</label>
      <input
        ref={inputRef}
        type={type}
        value={internalValue}
        onChange={handleChange}
        className="input"
        placeholder={placeholder}
        autoComplete="off"
      />
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );
});

const SelectField = ({ label, value, onChange, options, help }) => (
  <div>
    <label className="label">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="input">
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
  </div>
);

// Helper function to mask wallet addresses (show first 3 and last 3 characters)
const maskWalletAddress = (address) => {
  if (!address || address === 'Not set') return address;
  if (address.length <= 6) return address;
  return `${address.substring(0, 3)}${'•'.repeat(Math.min(address.length - 6, 40))}${address.substring(address.length - 3)}`;
};

const Settings = () => {
  // Debug re-renders
  console.log('Settings component rendering at:', new Date().toISOString());

  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('profile');
  const queryClient = useQueryClient();
  const { user, updateUser} = useAuth();
  const [avatarUploading, setAvatarUploading] = useState(false);
  const profileFormRef = React.useRef();
  const [arweaveBalance, setArweaveBalance] = useState(null);
  const [walletUploading, setWalletUploading] = useState(false);
  const [showWalletUpload, setShowWalletUpload] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showMnemonicEdit, setShowMnemonicEdit] = useState(false);
  const [showAlgoAddressEdit, setShowAlgoAddressEdit] = useState(false);
  const [showArweaveAddressEdit, setShowArweaveAddressEdit] = useState(false);
  
  // Simple isolated state for testing
  const [testFullName, setTestFullName] = useState('');
  
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    bio: '',
    avatar: null
  });

  // Mnemonic visibility state
  const [mnemonicVisible, setMnemonicVisible] = useState(false);

  // Wallet address visibility states
  const [algoAddressVisible, setAlgoAddressVisible] = useState(false);
  const [arweaveAddressVisible, setArweaveAddressVisible] = useState(false);

  // API key visibility states for Services tab
  const [anthropicKeyVisible, setAnthropicKeyVisible] = useState(false);
  const [mapboxKeyVisible, setMapboxKeyVisible] = useState(false);
  const [googleMapsKeyVisible, setGoogleMapsKeyVisible] = useState(false);
  const [walletConnectKeyVisible, setWalletConnectKeyVisible] = useState(false);

  // API key data states
  const [anthropicKeyData, setAnthropicKeyData] = useState({
    exists: false,
    masked: null,
    full: null,
    lastUpdated: null,
    source: null,
    loading: true
  });
  const [replicateKeyData, setReplicateKeyData] = useState({
    exists: false,
    masked: null,
    full: null,
    lastUpdated: null,
    source: null,
    loading: true
  });
  const [googleMapsKeyData, setGoogleMapsKeyData] = useState({
    exists: false,
    masked: null,
    full: null,
    lastUpdated: null,
    source: null,
    loading: true
  });
  const [walletConnectKeyData, setWalletConnectKeyData] = useState({
    exists: false,
    masked: null,
    full: null,
    lastUpdated: null,
    source: null,
    loading: true
  });
  const [replicateKeyVisible, setReplicateKeyVisible] = useState(false);
  const [unsplashKeyVisible, setUnsplashKeyVisible] = useState(false);
  const [unsplashKeyData, setUnsplashKeyData] = useState({
    exists: false,
    masked: null,
    full: null,
    lastUpdated: null,
    source: null,
    loading: true
  });
  const [mapboxKeyData, setMapboxKeyData] = useState({
    exists: false,
    masked: null,
    full: null,
    lastUpdated: null,
    source: null,
    loading: true
  });
  const [showUpdateKeyModal, setShowUpdateKeyModal] = useState(null); // null, 'anthropic', 'replicate', 'googlemaps', 'walletconnect', 'unsplash', 'mapbox'
  const [newApiKey, setNewApiKey] = useState('');
  const [updatingKey, setUpdatingKey] = useState(false);

  // Initialize profile data from user only once when user is available
  const [profileInitialized, setProfileInitialized] = useState(false);
  
  React.useEffect(() => {
    console.log('useEffect triggered - user:', !!user, 'profileInitialized:', profileInitialized);
    if (user && !profileInitialized) {
      console.log('Setting profile data from user:', user);

      // The avatar field comes from the login response as 'avatar' (mapped from avatar_url)
      let avatarUrl = user.avatar || null;
      console.log('Avatar URL from user object:', avatarUrl);

      if (avatarUrl && !avatarUrl.startsWith('http')) {
        const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
        avatarUrl = `${baseUrl}${avatarUrl}`;
        console.log('Converted avatar URL:', avatarUrl);
      }

      setProfileData({
        fullName: user.fullName || '',
        email: user.email || '',
        bio: user.bio || '',
        avatar: avatarUrl
      });
      setTestFullName(user.fullName || '');
      setProfileInitialized(true);
      console.log('Profile data initialized with avatar:', avatarUrl);
    }
  }, [user, profileInitialized]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settings, setSettings] = useState({
    general: {
      siteName: 'gamimap',
      siteDescription: 'GPS-based gamification platform',
      contactEmail: 'admin@gamimap.com',
      timezone: 'Europe/Berlin',
      language: 'en'
    },
    users: {
      allowRegistration: true,
      requireEmailVerification: true,
      allowPrivacyControls: true,
      maxProfileBioLength: 500
    },
    achievements: {
      autoVerifyThreshold: 70,
      nftMintingEnabled: true,
      requireWalletConnection: true,
      maxAchievementsPerDay: 20,
      duplicateDetectionWindow: 24
    },
    challenges: {
      challengeApprovalRequired: false,
      maxChallengeRadius: 1000,
      minChallengeRadius: 10,
      defaultRadius: 100,
      maxChallengesPerUser: 10
    },
    security: {
      locationAccuracyThreshold: 100,
      antiSpoofingEnabled: true,
      rateLimitWindow: 15,
      rateLimitRequests: 100,
      sessionTimeout: 7
    },
    blockchain: {
      activeChain: 'algorand',
      activeNetwork: 'testnet',
      walletMnemonic: '',
      walletAddress: '',
      nftMintingEnabled: true,
      nftNamePrefix: '',
      autoMintThreshold: 80,
      retryAttempts: 3,
      testnetMode: true,
      rpcEndpoint: 'https://testnet-api.algonode.cloud',
      indexerEndpoint: 'https://testnet-idx.algonode.cloud',
      arweaveWalletAddress: 'BTc6Ph8NynS5ZSz0sLCZ7FOjJ8QdffGbGDqCqsxsOeI',
      arweaveEnabled: false,
      // Filebase IPFS (primary storage)
      filebaseEnabled: true,
      filebaseBucket: 'gamimap-nft',
      storageProvider: 'filebase'
    },
    email: {
      senderName: 'gamimap',
      senderEmail: 'explore@exploreandwin.com'
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      achievementNotifications: true,
      adminAlerts: true
    }
  });

  // Fetch settings from backend
  const { data: backendSettings, isLoading: settingsLoading } = useQuery(
    'admin-settings',
    () => settingsAPI.getSettings(),
    {
      select: (data) => data.data.settings,
      staleTime: 5 * 60 * 1000, // 5 minutes - allow more frequent updates
      cacheTime: 30 * 60 * 1000, // 30 minutes cache
      refetchOnWindowFocus: false,
      refetchOnMount: 'always', // Always refetch when component mounts to get latest data
      refetchInterval: false, // Disable automatic refetch
      retry: false,
    }
  );

  // Update settings only once when data is loaded for the first time
  React.useEffect(() => {
    if (backendSettings && !settingsLoaded && Object.keys(backendSettings).length > 0) {
      // Merge backend settings with defaults to ensure all categories exist
      setSettings(prevSettings => ({
        ...prevSettings,
        ...backendSettings,
        // Ensure users category exists with defaults if not in backend
        users: {
          ...prevSettings.users,
          ...backendSettings.users
        }
      }));
      setSettingsLoaded(true);
    }
  }, [backendSettings, settingsLoaded]);

  // Mutation for saving settings
  const saveSettingsMutation = useMutation(
    ({ category, settingsData }) => {
      console.log('Mutation executing for category:', category, 'with data:', settingsData);
      return settingsAPI.saveSettings(category, settingsData);
    },
    {
      onSuccess: (data, variables) => {
        console.log('Settings save successful:', data, variables);
        toast.success(`${variables.category} settings saved successfully!`);
        // Don't invalidate query to prevent form reset - settings state is already updated locally
      },
      onError: (error) => {
        console.error('Settings save failed:', error);
        toast.error(`Failed to save settings: ${error.response?.data?.error || error.message}`);
        // On error, refetch to restore original values
        queryClient.invalidateQueries('admin-settings');
      }
    }
  );

  // Fetch system health data
  const { data: systemHealth, isLoading: healthLoading } = useQuery(
    'system-health',
    async () => {
      // This would normally call a system health endpoint
      return {
        database: { status: 'healthy', responseTime: 45 },
        api: { status: 'healthy', responseTime: 120 },
        redis: { status: 'healthy', responseTime: 12 },
        storage: { status: 'healthy', usage: 65 },
        blockchain: { status: 'healthy', lastSync: new Date() }
      };
    },
    { refetchInterval: 30000 }
  );

  // Fetch user statistics - using same pattern as Dashboard
  const { data: userStatsData, isLoading: userStatsLoading, error: userStatsError } = useQuery(
    'admin-user-stats',
    async () => {
      const response = await usersAPI.getUsers({ limit: 1, page: 1 });
      console.log('User stats raw response:', response);
      return response.data; // Extract data like Dashboard does
    },
    { 
      refetchInterval: 60000, // Auto-refresh every minute
      onError: (error) => console.error('User stats error:', error)
    }
  );
  
  const userStats = userStatsData?.pagination;

  // Fetch NFT statistics
  const { data: nftStats } = useQuery(
    'admin-nft-stats',
    () => nftsAPI.getNFTStats()
  );

  // Fetch ALGO balance when blockchain tab is active
  React.useEffect(() => {
    const fetchAlgoBalance = async () => {
      if (activeTab === 'blockchain' && settings.blockchain.walletAddress && settings.blockchain.activeChain === 'algorand') {
        try {
          const endpoint = settings.blockchain.testnetMode ? 'testnet' : 'mainnet';
          const response = await fetch(`https://${endpoint}-api.algonode.cloud/v2/accounts/${settings.blockchain.walletAddress}`);
          
          if (response.ok) {
            const data = await response.json();
            const balanceElement = document.getElementById('algo-balance');
            if (balanceElement) {
              const algoBalance = (data.amount / 1000000).toFixed(6); // Convert microAlgos to Algos
              balanceElement.textContent = `${algoBalance} ALGO`;
              balanceElement.className = 'font-mono text-green-600';
            }
          } else {
            const balanceElement = document.getElementById('algo-balance');
            if (balanceElement) {
              balanceElement.textContent = 'Error fetching balance';
              balanceElement.className = 'font-mono text-red-500';
            }
          }
        } catch (error) {
          console.error('Error fetching ALGO balance:', error);
          const balanceElement = document.getElementById('algo-balance');
          if (balanceElement) {
            balanceElement.textContent = 'Network error';
            balanceElement.className = 'font-mono text-red-500';
          }
        }
      }
    };

    fetchAlgoBalance();
  }, [activeTab, settings.blockchain.walletAddress, settings.blockchain.activeChain, settings.blockchain.testnetMode]);

  // Role-based tab filtering
  const userRole = user?.role || 'editor';

  const allTabs = [
    { id: 'profile', nameKey: 'cms.settings.account', icon: FiUser, roles: ['master_admin', 'admin', 'editor'] },
    { id: 'general', name: 'General', icon: FiSettings, roles: ['master_admin', 'admin'] },
    { id: 'users', name: 'Users', icon: FiUsers, roles: ['master_admin', 'admin'] },
    { id: 'email', name: 'Email', icon: FiMail, roles: ['master_admin', 'admin'] },
    { id: 'achievements', name: 'Achievements', icon: FiActivity, roles: ['master_admin', 'admin'] },
    { id: 'security', name: 'Security', icon: FiShield, roles: ['master_admin', 'admin'] },
    { id: 'services', name: 'Services', icon: FiKey, roles: ['master_admin'], badge: 'Encrypted' },
    { id: 'blockchain', name: 'Blockchain', icon: FiLink, roles: ['master_admin'], badge: 'Master' },
    { id: 'system', name: 'System Health', icon: FiDatabase, roles: ['master_admin', 'admin'] }
  ];

  // Filter tabs based on user role
  const tabs = allTabs.filter(tab => tab.roles.includes(userRole));

  const handleSettingChange = useCallback((category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  }, []); // Empty deps array - function doesn't depend on external values

  const handleSaveSettings = useCallback((category) => {
    console.log(`Saving ${category} settings from form inputs`);
    
    if (saveSettingsMutation.isLoading) {
      console.log('Already saving, ignoring request');
      return;
    }

    // Collect current values from all StableInput fields
    const formInputs = document.querySelectorAll(`input[data-setting-key]`);
    const updatedSettings = { ...settings[category] };
    
    console.log(`Found ${formInputs.length} input fields to collect values from`);
    
    formInputs.forEach(input => {
      if (input.getCurrentValue && input.getSettingKey) {
        const settingKey = input.getSettingKey();
        const currentValue = input.getCurrentValue();
        const inputType = input.getType && input.getType();
        
        if (settingKey && currentValue !== '') {
          // Convert value based on input type
          let processedValue = currentValue;
          if (inputType === 'number') {
            processedValue = parseInt(currentValue) || 0;
          }
          
          updatedSettings[settingKey] = processedValue;
          console.log(`Collected ${settingKey}:`, processedValue);
        }
      }
    });

    console.log(`Saving ${category} settings:`, updatedSettings);
    
    // Update local settings state immediately for UI feedback
    setSettings(prev => ({
      ...prev,
      [category]: updatedSettings
    }));
    
    // Save to backend
    saveSettingsMutation.mutate({ 
      category, 
      settingsData: updatedSettings 
    });
  }, [settings, saveSettingsMutation, setSettings]);

  // Avatar upload handler
  const handleAvatarUpload = useCallback(async (file) => {
    if (!file) {
      // Handle avatar removal
      setAvatarUploading(true);
      try {
        await usersAPI.removeAvatar();
        setProfileData(prev => ({ ...prev, avatar: null }));
        // Update user context so avatar removal persists
        updateUser({ avatar: null });
        toast.success('Avatar removed successfully!');
      } catch (error) {
        console.error('Avatar removal failed:', error);
        toast.error('Failed to remove avatar. Please try again.');
      } finally {
        setAvatarUploading(false);
      }
      return;
    }

    setAvatarUploading(true);

    try {
      const response = await usersAPI.uploadAvatar(file);
      const { avatarUrl } = response.data;

      console.log('Avatar upload response:', response.data);

      // Use full URL for avatar display - remove /api suffix for static files
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
      const fullAvatarUrl = avatarUrl.startsWith('http') ? avatarUrl : `${baseUrl}${avatarUrl}`;

      console.log('Setting avatar URL:', fullAvatarUrl);
      setProfileData(prev => ({ ...prev, avatar: fullAvatarUrl }));

      // Update user context so avatar persists across page refreshes
      updateUser({ avatar: avatarUrl }); // Store relative path in context

      toast.success('Avatar uploaded successfully!');
    } catch (error) {
      console.error('Avatar upload failed:', error);
      toast.error('Failed to upload avatar. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  }, [updateUser]);

  // Fetch Arweave balance
  const fetchArweaveBalance = useCallback(async () => {
    try {
      const response = await settingsAPI.getArweaveBalance();
      setArweaveBalance(response.data.balance);
    } catch (error) {
      console.error('Failed to fetch Arweave balance:', error);
    }
  }, []);

  // Handle Arweave wallet upload
  const handleWalletUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setWalletUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const walletData = JSON.parse(e.target.result);
          const response = await settingsAPI.uploadArweaveWallet(walletData);

          toast.success(response.data.message);

          // Update address in settings
          setSettings(prev => ({
            ...prev,
            blockchain: {
              ...prev.blockchain,
              arweaveWalletAddress: response.data.address
            }
          }));

          // Refresh balance
          fetchArweaveBalance();

          // Clear file input
          event.target.value = '';
        } catch (parseError) {
          toast.error('Invalid wallet file format');
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Wallet upload failed:', error);
      toast.error(error.response?.data?.error || 'Failed to upload wallet');
    } finally {
      setWalletUploading(false);
    }
  }, [fetchArweaveBalance]);

  // Load balance when Blockchain tab is active
  React.useEffect(() => {
    if (activeTab === 'blockchain' && settings.blockchain?.arweaveEnabled) {
      fetchArweaveBalance();
    }
  }, [activeTab, settings.blockchain?.arweaveEnabled, fetchArweaveBalance]);

  // Load API keys when Services tab is active
  React.useEffect(() => {
    if (activeTab === 'services') {
      const fetchAnthropicKey = async () => {
        try {
          setAnthropicKeyData(prev => ({ ...prev, loading: true }));
          const response = await secureSettingsAPI.getAnthropicKey();
          setAnthropicKeyData({
            exists: response.data.exists,
            masked: response.data.masked,
            full: response.data.masked, // Backend only returns masked, full key not exposed
            lastUpdated: response.data.lastUpdated,
            source: response.data.source,
            loading: false
          });
        } catch (error) {
          console.error('Error fetching Anthropic API key:', error);
          setAnthropicKeyData(prev => ({ ...prev, loading: false, exists: false }));
          toast.error('Failed to load API key status');
        }
      };

      const fetchReplicateKey = async () => {
        try {
          setReplicateKeyData(prev => ({ ...prev, loading: true }));
          const response = await secureSettingsAPI.getReplicateKey();
          setReplicateKeyData({
            exists: response.data.exists,
            masked: response.data.masked,
            full: response.data.masked,
            lastUpdated: response.data.lastUpdated,
            source: response.data.source,
            loading: false
          });
        } catch (error) {
          console.error('Error fetching Replicate API key:', error);
          setReplicateKeyData(prev => ({ ...prev, loading: false, exists: false }));
        }
      };

      const fetchGoogleMapsKey = async () => {
        try {
          setGoogleMapsKeyData(prev => ({ ...prev, loading: true }));
          const response = await secureSettingsAPI.getGoogleMapsKey();
          setGoogleMapsKeyData({
            exists: response.data.exists,
            masked: response.data.masked,
            full: response.data.masked,
            lastUpdated: response.data.lastUpdated,
            source: response.data.source,
            loading: false
          });
        } catch (error) {
          console.error('Error fetching Google Maps API key:', error);
          setGoogleMapsKeyData(prev => ({ ...prev, loading: false, exists: false }));
        }
      };

      const fetchWalletConnectKey = async () => {
        try {
          setWalletConnectKeyData(prev => ({ ...prev, loading: true }));
          const response = await secureSettingsAPI.getWalletConnectKey();
          setWalletConnectKeyData({
            exists: response.data.exists,
            masked: response.data.masked,
            full: response.data.masked,
            lastUpdated: response.data.lastUpdated,
            source: response.data.source,
            loading: false
          });
        } catch (error) {
          console.error('Error fetching WalletConnect Project ID:', error);
          setWalletConnectKeyData(prev => ({ ...prev, loading: false, exists: false }));
        }
      };

      const fetchUnsplashKey = async () => {
        try {
          setUnsplashKeyData(prev => ({ ...prev, loading: true }));
          const response = await secureSettingsAPI.getUnsplashKey();
          setUnsplashKeyData({
            exists: response.data.exists,
            masked: response.data.masked,
            full: response.data.masked,
            lastUpdated: response.data.lastUpdated,
            source: response.data.source,
            loading: false
          });
        } catch (error) {
          console.error('Error fetching Unsplash API key:', error);
          setUnsplashKeyData(prev => ({ ...prev, loading: false, exists: false }));
        }
      };

      const fetchMapboxKey = async () => {
        try {
          setMapboxKeyData(prev => ({ ...prev, loading: true }));
          const response = await secureSettingsAPI.getMapboxKey();
          setMapboxKeyData({
            exists: response.data.exists,
            masked: response.data.masked,
            full: response.data.full,
            lastUpdated: response.data.lastUpdated,
            source: response.data.source,
            loading: false
          });
        } catch (error) {
          console.error('Error fetching MapBox API key:', error);
          setMapboxKeyData(prev => ({ ...prev, loading: false, exists: false }));
        }
      };

      fetchAnthropicKey();
      fetchReplicateKey();
      fetchGoogleMapsKey();
      fetchWalletConnectKey();
      fetchUnsplashKey();
      fetchMapboxKey();
    }
  }, [activeTab]);

  // Profile update handler
  const handleProfileUpdate = useCallback(async () => {
    try {
      // Get form data from the ProfileForm ref
      const formData = profileFormRef.current?.getFormData();
      
      if (!formData) {
        toast.error('Unable to get form data. Please try again.');
        return;
      }
      
      console.log('Saving profile data:', formData);
      
      // Make API call to update profile (use admin endpoint for CMS users)
      const response = await usersAPI.updateAdminProfile(formData);
      
      console.log('Profile update response:', response.data);
      toast.success('Profile updated successfully!');
      
      // Update the user context with the new data
      updateUser({ ...user, ...formData });
    } catch (error) {
      console.error('Profile update failed:', error);
      toast.error(`Failed to update profile: ${error.response?.data?.error || error.message}`);
    }
  }, [user, updateUser]);

  const handleProfileChange = useCallback((key, value) => {
    setProfileData(prev => ({ ...prev, [key]: value }));
  }, []);

  // Services tab handlers
  const handleUpdateAnthropicKey = useCallback(async () => {
    if (!newApiKey || !newApiKey.startsWith('sk-ant-')) {
      toast.error('Please enter a valid Anthropic API key (starts with sk-ant-)');
      return;
    }

    try {
      setUpdatingKey(true);
      const response = await secureSettingsAPI.updateAnthropicKey(newApiKey);
      toast.success(response.data.message || 'API key updated successfully');

      // Refresh the key data
      setAnthropicKeyData({
        exists: true,
        masked: response.data.masked,
        full: response.data.masked,
        lastUpdated: new Date().toISOString(),
        source: 'database (encrypted)',
        loading: false
      });

      // Close modal and reset
      setShowUpdateKeyModal(null);
      setNewApiKey('');
      setAnthropicKeyVisible(false);
    } catch (error) {
      console.error('Error updating API key:', error);
      toast.error(error.response?.data?.error || 'Failed to update API key');
    } finally {
      setUpdatingKey(false);
    }
  }, [newApiKey]);

  const handleUpdateWalletConnectKey = useCallback(async () => {
    // WalletConnect Project IDs are 32-char hex strings
    if (!newApiKey || !/^[a-f0-9]{32}$/.test(newApiKey)) {
      toast.error('Please enter a valid WalletConnect Project ID (32 character hex string)');
      return;
    }

    try {
      setUpdatingKey(true);
      const response = await secureSettingsAPI.updateWalletConnectKey(newApiKey);
      toast.success(response.data.message || 'Project ID updated successfully');

      // Refresh the key data
      setWalletConnectKeyData({
        exists: true,
        masked: response.data.masked,
        full: response.data.masked,
        lastUpdated: new Date().toISOString(),
        source: 'database (encrypted)',
        loading: false
      });

      // Close modal and reset
      setShowUpdateKeyModal(null);
      setNewApiKey('');
      setWalletConnectKeyVisible(false);
    } catch (error) {
      console.error('Error updating WalletConnect Project ID:', error);
      toast.error(error.response?.data?.error || 'Failed to update Project ID');
    } finally {
      setUpdatingKey(false);
    }
  }, [newApiKey]);

  const handleUpdateReplicateKey = useCallback(async () => {
    if (!newApiKey || !newApiKey.startsWith('r8_')) {
      toast.error('Please enter a valid Replicate API key (starts with r8_)');
      return;
    }

    try {
      setUpdatingKey(true);
      const response = await secureSettingsAPI.updateReplicateKey(newApiKey);
      toast.success(response.data.message || 'API key updated successfully');

      // Refresh the key data
      setReplicateKeyData({
        exists: true,
        masked: response.data.masked,
        full: response.data.masked,
        lastUpdated: new Date().toISOString(),
        source: 'database (encrypted)',
        loading: false
      });

      // Close modal and reset
      setShowUpdateKeyModal(null);
      setNewApiKey('');
      setReplicateKeyVisible(false);
    } catch (error) {
      console.error('Error updating Replicate API key:', error);
      toast.error(error.response?.data?.error || 'Failed to update API key');
    } finally {
      setUpdatingKey(false);
    }
  }, [newApiKey]);

  const handleUpdateGoogleMapsKey = useCallback(async () => {
    if (!newApiKey || !newApiKey.startsWith('AIza')) {
      toast.error('Please enter a valid Google Maps API key (starts with AIza)');
      return;
    }

    try {
      setUpdatingKey(true);
      const response = await secureSettingsAPI.updateGoogleMapsKey(newApiKey);
      toast.success(response.data.message || 'API key updated successfully');

      // Refresh the key data
      setGoogleMapsKeyData({
        exists: true,
        masked: response.data.masked,
        full: response.data.masked,
        lastUpdated: new Date().toISOString(),
        source: 'database (encrypted)',
        loading: false
      });

      // Close modal and reset
      setShowUpdateKeyModal(null);
      setNewApiKey('');
      setGoogleMapsKeyVisible(false);
    } catch (error) {
      console.error('Error updating Google Maps API key:', error);
      toast.error(error.response?.data?.error || 'Failed to update API key');
    } finally {
      setUpdatingKey(false);
    }
  }, [newApiKey]);

  const handleUpdateUnsplashKey = useCallback(async () => {
    if (!newApiKey) {
      toast.error('Please enter an Unsplash Access Key');
      return;
    }

    try {
      setUpdatingKey(true);
      const response = await secureSettingsAPI.updateUnsplashKey(newApiKey);
      toast.success(response.data.message || 'Unsplash API key updated successfully');

      // Refresh the key data
      setUnsplashKeyData({
        exists: true,
        masked: response.data.masked,
        full: response.data.masked,
        lastUpdated: new Date().toISOString(),
        source: 'database (encrypted)',
        loading: false
      });

      // Close modal and reset
      setShowUpdateKeyModal(null);
      setNewApiKey('');
      setUnsplashKeyVisible(false);
    } catch (error) {
      console.error('Error updating Unsplash API key:', error);
      toast.error(error.response?.data?.error || 'Failed to update API key');
    } finally {
      setUpdatingKey(false);
    }
  }, [newApiKey]);

  const handleUpdateMapboxKey = useCallback(async () => {
    if (!newApiKey || !newApiKey.startsWith('pk.')) {
      toast.error('Please enter a valid MapBox API key (starts with pk.)');
      return;
    }

    try {
      setUpdatingKey(true);
      const response = await secureSettingsAPI.updateMapboxKey(newApiKey);
      toast.success(response.data.message || 'MapBox API key updated successfully');

      setMapboxKeyData({
        exists: true,
        masked: response.data.masked,
        full: newApiKey,
        lastUpdated: new Date().toISOString(),
        source: 'database (encrypted)',
        loading: false
      });

      setShowUpdateKeyModal(null);
      setNewApiKey('');
      setMapboxKeyVisible(false);
    } catch (error) {
      console.error('Error updating MapBox API key:', error);
      toast.error(error.response?.data?.error || 'Failed to update API key');
    } finally {
      setUpdatingKey(false);
    }
  }, [newApiKey]);

  const handleTestConnection = useCallback(async (service) => {
    // For now, just show a toast. Real implementation would call backend test endpoint
    toast.loading('Testing connection...');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (service === 'anthropic') {
        if (!anthropicKeyData.exists) {
          toast.error('No API key configured');
          return;
        }
        // In real implementation, would call: await secureSettingsAPI.testAnthropicConnection();
        toast.success('Connection successful! Claude API is responding.');
      } else if (service === 'mapbox') {
        toast.success('MapBox connection successful!');
      }
    } catch (error) {
      toast.error(`Connection test failed: ${error.message}`);
    }
  }, [anthropicKeyData.exists]);

  // Create stable onChange handlers
  const handleFullNameChange = useCallback((value) => {
    handleProfileChange('fullName', value);
  }, [handleProfileChange]);

  const handleEmailChange = useCallback((value) => {
    handleProfileChange('email', value);
  }, [handleProfileChange]);

  const handleFullNameWorkingChange = useCallback((value) => {
    setProfileData(prev => ({ ...prev, fullName: value }));
  }, []);

  const SettingCard = ({ title, children, onSave, category }) => (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <button
          onClick={() => onSave(category)}
          disabled={saveSettingsMutation.isLoading}
          className="btn btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiSave className="w-4 h-4" />
          <span>{saveSettingsMutation.isLoading ? 'Saving...' : t('cms.common.save')}</span>
        </button>
      </div>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );


  const ToggleField = ({ label, value, onChange, help }) => (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {help && <p className="text-xs text-gray-500">{help}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
          ${value ? 'bg-primary-500' : 'bg-gray-200'}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
            ${value ? 'translate-x-6' : 'translate-x-1'}
          `}
        />
      </button>
    </div>
  );

  const StatusIndicator = ({ status, label, detail }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'healthy': return 'bg-green-500';
        case 'warning': return 'bg-yellow-500';
        case 'error': return 'bg-red-500';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`}></div>
          <div>
            <div className="text-sm font-medium text-gray-900">{label}</div>
            {detail && <div className="text-xs text-gray-500">{detail}</div>}
          </div>
        </div>
        <div className="text-sm text-gray-600">
          {status === 'healthy' && <FiCheck className="w-4 h-4 text-green-600" />}
          {status === 'warning' && <FiAlertTriangle className="w-4 h-4 text-yellow-600" />}
          {status === 'error' && <FiX className="w-4 h-4 text-red-600" />}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <SettingCard
            title={t('cms.settings.account')}
            category="profile"
            onSave={handleProfileUpdate}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Avatar Upload Section */}
              <div className="lg:col-span-1">
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">{t('cms.settings.avatar')}</h3>
                  <AvatarUpload
                    currentAvatar={profileData.avatar}
                    onUpload={handleAvatarUpload}
                    loading={avatarUploading}
                    size="large"
                  />
                </div>
              </div>

              {/* Profile Information Section */}
              <div className="lg:col-span-2 space-y-4">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Profile Information</h3>
                
                <ProfileForm ref={profileFormRef} user={user} t={t} />

                {/* Current User Info Display */}
                <div className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-200">
                  <h4 className="text-sm font-medium text-primary-900 mb-2">Current Session</h4>
                  <div className="text-xs text-primary-700 space-y-1">
                    <div>Username: {user?.username}</div>
                    <div>{t('cms.settings.role')}: {user?.role}</div>
                    <div>Last Login: {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Unknown'}</div>
                  </div>
                </div>
              </div>
            </div>
          </SettingCard>
        );

      case 'general':
        return (
          <SettingCard
            title="General Settings"
            category="general"
            onSave={handleSaveSettings}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StableInput
                label="Site Name"
                initialValue={settings.general.siteName}
                placeholder="gamimap"
                tabKey={activeTab}
                settingKey="siteName"
              />
              <StableInput
                label="Contact Email"
                initialValue={settings.general.contactEmail}
                type="email"
                placeholder="admin@example.com"
                tabKey={activeTab}
                settingKey="contactEmail"
              />
            </div>
            <StableInput
              label="Site Description"
              initialValue={settings.general.siteDescription}
              placeholder="GPS-based gamification platform"
              tabKey={activeTab}
              settingKey="siteDescription"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label={t('cms.settings.timezone')}
                value={settings.general.timezone}
                onChange={(value) => handleSettingChange('general', 'timezone', value)}
                options={[
                  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
                  { value: 'UTC', label: 'UTC' },
                  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
                  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' }
                ]}
              />
              <SelectField
                label="Default Language"
                value={settings.general.language}
                onChange={(value) => handleSettingChange('general', 'language', value)}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'de', label: 'Deutsch' },
                  { value: 'es', label: 'Español' },
                  { value: 'fr', label: 'Français' }
                ]}
              />
            </div>
          </SettingCard>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">
                    {userStatsLoading ? '...' : (userStats?.totalUsers || 0)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Total Users {userStatsError ? '(Error)' : ''}
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{nftStats?.overall?.uniqueCollectors || 0}</div>
                  <div className="text-sm text-gray-600">NFT Collectors</div>
                </div>
              </div>
              <div className="card">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">
                    {nftStats?.overall?.totalNftsMinted || 0}
                  </div>
                  <div className="text-sm text-gray-600">NFTs Minted</div>
                </div>
              </div>
            </div>

            <SettingCard
              title="User Management Settings"
              category="users"
              onSave={handleSaveSettings}
            >
              <ToggleField
                label="Allow User Registration"
                value={settings.users?.allowRegistration ?? true}
                onChange={(value) => handleSettingChange('users', 'allowRegistration', value)}
                help="Enable new users to register accounts"
              />
              <ToggleField
                label="Email Verification Required"
                value={settings.users?.requireEmailVerification ?? true}
                onChange={(value) => handleSettingChange('users', 'requireEmailVerification', value)}
                help="Require email verification for new accounts"
              />
              <ToggleField
                label="Profile Privacy Controls"
                value={settings.users?.allowPrivacyControls ?? true}
                onChange={(value) => handleSettingChange('users', 'allowPrivacyControls', value)}
                help="Allow users to set profile visibility"
              />
              <StableInput
                label="Maximum Profile Bio Length"
                initialValue={settings.users?.maxProfileBioLength || 500}
                type="number"
                placeholder="Maximum characters allowed in user bio"
                tabKey={activeTab}
                settingKey="maxProfileBioLength"
              />
            </SettingCard>
          </div>
        );

      case 'achievements':
        return (
          <SettingCard
            title="Achievement & NFT Settings"
            category="achievements"
            onSave={handleSaveSettings}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StableInput
                label="Auto-Verification Threshold"
                initialValue={settings.achievements.autoVerifyThreshold}
                type="number"
                placeholder="Minimum verification score (0-100) for automatic approval"
                tabKey={activeTab}
                settingKey="autoVerifyThreshold"
              />
              <StableInput
                label="Max Achievements Per Day"
                initialValue={settings.achievements.maxAchievementsPerDay}
                type="number"
                placeholder="Maximum achievements a user can earn in 24 hours"
                tabKey={activeTab}
                settingKey="maxAchievementsPerDay"
              />
            </div>
            <ToggleField
              label="NFT Minting Enabled"
              value={settings.achievements.nftMintingEnabled}
              onChange={(value) => handleSettingChange('achievements', 'nftMintingEnabled', value)}
              help="Enable NFT minting for verified achievements"
            />
            <ToggleField
              label="Require Wallet Connection"
              value={settings.achievements.requireWalletConnection}
              onChange={(value) => handleSettingChange('achievements', 'requireWalletConnection', value)}
              help="Require users to connect blockchain wallet before earning NFTs"
            />
            <StableInput
              label="Duplicate Detection Window (hours)"
              initialValue={settings.achievements.duplicateDetectionWindow}
              type="number"
              placeholder="Time window to prevent duplicate achievements"
              tabKey={activeTab}
              settingKey="duplicateDetectionWindow"
            />
          </SettingCard>
        );

      case 'email':
        return (
          <SettingCard
            title="Email Configuration"
            category="email"
            onSave={handleSaveSettings}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StableInput
                label="Sender Name"
                initialValue={settings.email.senderName}
                placeholder="gamimap"
                tabKey={activeTab}
                settingKey="senderName"
              />
              <StableInput
                label="Sender Email Address"
                initialValue={settings.email.senderEmail}
                type="email"
                placeholder="explore@exploreandwin.com"
                tabKey={activeTab}
                settingKey="senderEmail"
              />
            </div>
          </SettingCard>
        );

      case 'security':
        return (
          <SettingCard
            title="Security & Anti-Spoofing Settings"
            category="security"
            onSave={handleSaveSettings}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StableInput
                label="Location Accuracy Threshold (meters)"
                initialValue={settings.security.locationAccuracyThreshold}
                type="number"
                placeholder="Maximum GPS accuracy required for achievements"
                tabKey={activeTab}
                settingKey="locationAccuracyThreshold"
              />
              <StableInput
                label="Session Timeout (days)"
                initialValue={settings.security.sessionTimeout}
                type="number"
                placeholder="How long refresh tokens remain valid"
                tabKey={activeTab}
                settingKey="sessionTimeout"
              />
            </div>
            <ToggleField
              label="Anti-Spoofing Detection"
              value={settings.security.antiSpoofingEnabled}
              onChange={(value) => handleSettingChange('security', 'antiSpoofingEnabled', value)}
              help="Enable advanced location verification algorithms"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StableInput
                label="Rate Limit Window (minutes)"
                initialValue={settings.security.rateLimitWindow}
                type="number"
                placeholder="Time window for rate limiting"
                tabKey={activeTab}
                settingKey="rateLimitWindow"
              />
              <StableInput
                label="Rate Limit Requests"
                initialValue={settings.security.rateLimitRequests}
                type="number"
                placeholder="Maximum requests per window"
                tabKey={activeTab}
                settingKey="rateLimitRequests"
              />
            </div>
          </SettingCard>
        );

      case 'services':
        return (
          <div className="space-y-6">
            {/* AI Services Card */}
            <SettingCard
              title="AI Services"
              description="Configure AI-powered features"
            >
              <div className="space-y-4">
                {/* Anthropic Claude API */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Anthropic Claude API</h4>
                      <p className="text-xs text-gray-500">Powers AI-generated challenges</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {anthropicKeyData.loading ? (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Loading...</span>
                      ) : anthropicKeyData.exists ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Not Configured</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type={anthropicKeyVisible ? "text" : "password"}
                            value={anthropicKeyData.loading ? "Loading..." : (anthropicKeyVisible ? anthropicKeyData.full : anthropicKeyData.masked) || "Not configured"}
                            readOnly
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                          />
                          {anthropicKeyData.exists && (
                            <button
                              type="button"
                              onClick={() => setAnthropicKeyVisible(!anthropicKeyVisible)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {anthropicKeyVisible ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setShowUpdateKeyModal('anthropic')}
                          className="btn btn-primary text-sm"
                        >
                          {anthropicKeyData.exists ? 'Update' : 'Add Key'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {anthropicKeyData.loading ? 'Loading...' : anthropicKeyData.lastUpdated ? `Last updated: ${new Date(anthropicKeyData.lastUpdated).toLocaleString()}` : 'Not configured'}
                      </p>
                      {anthropicKeyData.source && (
                        <p className="text-xs text-primary-600 mt-0.5">Source: {anthropicKeyData.source}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleTestConnection('anthropic')}
                        disabled={!anthropicKeyData.exists || anthropicKeyData.loading}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Test Connection
                      </button>
                      <button
                        onClick={() => toast('Usage tracking coming soon', { icon: 'ℹ️' })}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        View Usage
                      </button>
                      <a
                        href="https://console.anthropic.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        Get API Key →
                      </a>
                    </div>
                  </div>
                </div>

                {/* Replicate API */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Replicate API</h4>
                      <p className="text-xs text-gray-500">Powers AI image cartoonification for NFTs</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {replicateKeyData.loading ? (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Loading...</span>
                      ) : replicateKeyData.exists ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Not Configured</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type={replicateKeyVisible ? "text" : "password"}
                            value={replicateKeyData.loading ? "Loading..." : (replicateKeyVisible ? replicateKeyData.full : replicateKeyData.masked) || "Not configured"}
                            readOnly
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                          />
                          {replicateKeyData.exists && (
                            <button
                              type="button"
                              onClick={() => setReplicateKeyVisible(!replicateKeyVisible)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {replicateKeyVisible ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setShowUpdateKeyModal('replicate')}
                          className="btn btn-primary text-sm"
                        >
                          {replicateKeyData.exists ? 'Update' : 'Add Key'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {replicateKeyData.loading ? 'Loading...' : replicateKeyData.lastUpdated ? `Last updated: ${new Date(replicateKeyData.lastUpdated).toLocaleString()}` : 'Not configured'}
                      </p>
                      {replicateKeyData.source && (
                        <p className="text-xs text-primary-600 mt-0.5">Source: {replicateKeyData.source}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleTestConnection('replicate')}
                        disabled={!replicateKeyData.exists || replicateKeyData.loading}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Test Connection
                      </button>
                      <a
                        href="https://replicate.com/account/api-tokens"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        Get API Key →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </SettingCard>

            {/* Geocoding Services Card */}
            <SettingCard
              title="Geocoding Services"
              description="Configure location accuracy providers"
            >
              <div className="space-y-4">
                {/* MapBox API */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">MapBox API</h4>
                      <p className="text-xs text-gray-500">100,000 free requests/month - Recommended</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {mapboxKeyData.loading ? (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Loading...</span>
                      ) : mapboxKeyData.exists ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Not Configured</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type={mapboxKeyVisible ? "text" : "password"}
                            value={mapboxKeyData.loading ? "Loading..." : (mapboxKeyVisible ? mapboxKeyData.full : mapboxKeyData.masked) || "Not configured"}
                            readOnly
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                          />
                          {mapboxKeyData.exists && (
                            <button
                              type="button"
                              onClick={() => setMapboxKeyVisible(!mapboxKeyVisible)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {mapboxKeyVisible ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setShowUpdateKeyModal('mapbox')}
                          className="btn btn-primary text-sm"
                        >
                          {mapboxKeyData.exists ? 'Update' : 'Add Key'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {mapboxKeyData.loading ? 'Loading...' : mapboxKeyData.lastUpdated ? `Last updated: ${new Date(mapboxKeyData.lastUpdated).toLocaleString()}` : 'Not configured'}
                      </p>
                      {mapboxKeyData.source && (
                        <p className="text-xs text-primary-600 mt-0.5">Source: {mapboxKeyData.source}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleTestConnection('mapbox')}
                        disabled={!mapboxKeyData.exists || mapboxKeyData.loading}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        Test Connection
                      </button>
                      <a
                        href="https://account.mapbox.com/access-tokens/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        Get API Key →
                      </a>
                    </div>
                  </div>
                </div>

                {/* Google Maps API */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Google Maps API</h4>
                      <p className="text-xs text-gray-500">$5 per 1000 requests (200 free/day) - Geocoding</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {googleMapsKeyData.loading ? (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Loading...</span>
                      ) : googleMapsKeyData.exists ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Not Configured</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type={googleMapsKeyVisible ? "text" : "password"}
                            value={googleMapsKeyData.loading ? "Loading..." : (googleMapsKeyVisible ? googleMapsKeyData.full : googleMapsKeyData.masked) || "Not configured"}
                            readOnly
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                          />
                          {googleMapsKeyData.exists && (
                            <button
                              type="button"
                              onClick={() => setGoogleMapsKeyVisible(!googleMapsKeyVisible)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {googleMapsKeyVisible ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setShowUpdateKeyModal('googlemaps')}
                          className="btn btn-primary text-sm"
                        >
                          {googleMapsKeyData.exists ? 'Update' : 'Add Key'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {googleMapsKeyData.loading ? 'Loading...' : googleMapsKeyData.lastUpdated ? `Last updated: ${new Date(googleMapsKeyData.lastUpdated).toLocaleString()}` : 'Not configured'}
                      </p>
                      {googleMapsKeyData.source && (
                        <p className="text-xs text-primary-600 mt-0.5">Source: {googleMapsKeyData.source}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleTestConnection('googlemaps')}
                        disabled={!googleMapsKeyData.exists || googleMapsKeyData.loading}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Test Connection
                      </button>
                      <a
                        href="https://console.cloud.google.com/google/maps-apis/credentials"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        Get API Key →
                      </a>
                    </div>
                  </div>
                </div>

                {/* WalletConnect Project ID */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">WalletConnect</h4>
                      <p className="text-xs text-gray-500">Project ID for mobile wallet connections (Pera, Defly)</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {walletConnectKeyData.loading ? (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Loading...</span>
                      ) : walletConnectKeyData.exists ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Not Configured</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Project ID</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type={walletConnectKeyVisible ? "text" : "password"}
                            value={walletConnectKeyData.loading ? "Loading..." : (walletConnectKeyVisible ? walletConnectKeyData.full : walletConnectKeyData.masked) || "Not configured"}
                            readOnly
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                          />
                          {walletConnectKeyData.exists && (
                            <button
                              type="button"
                              onClick={() => setWalletConnectKeyVisible(!walletConnectKeyVisible)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {walletConnectKeyVisible ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setShowUpdateKeyModal('walletconnect')}
                          className="btn btn-primary text-sm"
                        >
                          {walletConnectKeyData.exists ? 'Update' : 'Add Key'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {walletConnectKeyData.loading ? 'Loading...' : walletConnectKeyData.lastUpdated ? `Last updated: ${new Date(walletConnectKeyData.lastUpdated).toLocaleString()}` : 'Not configured'}
                      </p>
                      {walletConnectKeyData.source && (
                        <p className="text-xs text-primary-600 mt-0.5">Source: {walletConnectKeyData.source}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                      <a
                        href="https://cloud.walletconnect.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        Get Project ID →
                      </a>
                    </div>
                  </div>
                </div>

                {/* Unsplash API */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Unsplash API</h4>
                      <p className="text-xs text-gray-500">Stock photos for challenge images (50 requests/hour limit)</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {unsplashKeyData.loading ? (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Loading...</span>
                      ) : unsplashKeyData.exists ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Not Configured</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Access Key</label>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type={unsplashKeyVisible ? "text" : "password"}
                            value={unsplashKeyData.loading ? "Loading..." : (unsplashKeyVisible ? unsplashKeyData.full : unsplashKeyData.masked) || "Not configured"}
                            readOnly
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                          />
                          {unsplashKeyData.exists && (
                            <button
                              type="button"
                              onClick={() => setUnsplashKeyVisible(!unsplashKeyVisible)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {unsplashKeyVisible ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setShowUpdateKeyModal('unsplash')}
                          className="btn btn-primary text-sm"
                        >
                          {unsplashKeyData.exists ? 'Update' : 'Add Key'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {unsplashKeyData.loading ? 'Loading...' : unsplashKeyData.lastUpdated ? `Last updated: ${new Date(unsplashKeyData.lastUpdated).toLocaleString()}` : 'Not configured'}
                      </p>
                      {unsplashKeyData.source && (
                        <p className="text-xs text-primary-600 mt-0.5">Source: {unsplashKeyData.source}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleTestConnection('unsplash')}
                        disabled={!unsplashKeyData.exists || unsplashKeyData.loading}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Test Connection
                      </button>
                      <a
                        href="https://unsplash.com/oauth/applications"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        Get API Key →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </SettingCard>

            {/* Security Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FiShield className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-green-900 mb-1">Security Features</h4>
                  <ul className="text-xs text-green-700 space-y-1">
                    <li>• AES-256-CBC encryption for all API keys</li>
                    <li>• Unique IV (Initialization Vector) per encryption</li>
                    <li>• Encrypted keys stored in database, never in plaintext</li>
                    <li>• Audit logging for all key changes (who, when, from where)</li>
                    <li>• Master admin role required for key management</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Update API Key Modal */}
            {showUpdateKeyModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {showUpdateKeyModal === 'anthropic' ? 'Update Anthropic API Key' :
                       showUpdateKeyModal === 'replicate' ? 'Update Replicate API Key' :
                       showUpdateKeyModal === 'googlemaps' ? 'Update Google Maps API Key' :
                       showUpdateKeyModal === 'walletconnect' ? 'Update WalletConnect Project ID' :
                       showUpdateKeyModal === 'unsplash' ? 'Update Unsplash API Key' :
                       showUpdateKeyModal === 'mapbox' ? 'Update MapBox API Key' : 'Update API Key'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowUpdateKeyModal(null);
                        setNewApiKey('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New API Key
                      </label>
                      <input
                        type="text"
                        value={newApiKey}
                        onChange={(e) => setNewApiKey(e.target.value)}
                        placeholder={showUpdateKeyModal === 'anthropic' ? 'sk-ant-api03-...' :
                                    showUpdateKeyModal === 'replicate' ? 'r8_...' :
                                    showUpdateKeyModal === 'googlemaps' ? 'AIza...' :
                                    showUpdateKeyModal === 'unsplash' ? 'Your Unsplash Access Key' :
                                    showUpdateKeyModal === 'mapbox' ? 'pk.eyJ1...' : 'Enter API key'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      />
                      {showUpdateKeyModal === 'anthropic' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Must start with "sk-ant-"
                        </p>
                      )}
                      {showUpdateKeyModal === 'replicate' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Must start with "r8_"
                        </p>
                      )}
                      {showUpdateKeyModal === 'googlemaps' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Must start with "AIza"
                        </p>
                      )}
                      {showUpdateKeyModal === 'mapbox' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Must start with "pk."
                        </p>
                      )}
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <div className="flex items-start">
                        <FiAlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-xs text-yellow-800">
                          <p className="font-medium mb-1">Security Note:</p>
                          <p>Your API key will be encrypted with AES-256-CBC and stored securely in the database. This action will be logged in the audit trail.</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 pt-2">
                      <button
                        onClick={showUpdateKeyModal === 'walletconnect' ? handleUpdateWalletConnectKey :
                                 showUpdateKeyModal === 'replicate' ? handleUpdateReplicateKey :
                                 showUpdateKeyModal === 'googlemaps' ? handleUpdateGoogleMapsKey :
                                 showUpdateKeyModal === 'unsplash' ? handleUpdateUnsplashKey :
                                 showUpdateKeyModal === 'mapbox' ? handleUpdateMapboxKey : handleUpdateAnthropicKey}
                        disabled={updatingKey || !newApiKey}
                        className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updatingKey ? 'Updating...' : 'Update Key'}
                      </button>
                      <button
                        onClick={() => {
                          setShowUpdateKeyModal(null);
                          setNewApiKey('');
                        }}
                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'blockchain':
        return <BlockchainSettingsTab />;

      case 'system':
        return (
          <div className="space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">System Health</h2>
                <button className="btn btn-secondary flex items-center space-x-2">
                  <FiRefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </button>
              </div>

              {healthLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-gray-200 rounded-lg p-4 h-16"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <StatusIndicator
                    status={systemHealth?.database?.status || 'error'}
                    label="Database"
                    detail={`Response time: ${systemHealth?.database?.responseTime || 0}ms`}
                  />
                  <StatusIndicator
                    status={systemHealth?.api?.status || 'error'}
                    label="API Server"
                    detail={`Response time: ${systemHealth?.api?.responseTime || 0}ms`}
                  />
                  <StatusIndicator
                    status={systemHealth?.redis?.status || 'error'}
                    label="Redis Cache"
                    detail={`Response time: ${systemHealth?.redis?.responseTime || 0}ms`}
                  />
                  <StatusIndicator
                    status={systemHealth?.storage?.status || 'error'}
                    label="File Storage"
                    detail={`Usage: ${systemHealth?.storage?.usage || 0}%`}
                  />
                  <StatusIndicator
                    status={systemHealth?.blockchain?.status || 'warning'}
                    label="Blockchain Network"
                    detail="NFT minting available"
                  />
                </div>
              )}
            </div>

            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">System Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-medium text-gray-900 mb-2">Application</div>
                  <div className="space-y-1 text-gray-600">
                    <div>Version: 1.0.0</div>
                    <div>Build: 2025.08.30</div>
                    <div>Environment: Development</div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-medium text-gray-900 mb-2">Database</div>
                  <div className="space-y-1 text-gray-600">
                    <div>Type: MySQL 8.0</div>
                    <div>Tables: 13</div>
                    <div>Status: Connected</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.settings')}</h1>
          <p className="text-gray-600">{t('cms.settings.subtitle')}</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 flex-shrink-0">
          <nav className="card p-4">
            <div className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors
                      ${activeTab === tab.id
                        ? 'bg-gradient-to-r from-primary-50 to-secondary-50 text-primary-700 border-r-2 border-primary-500'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-primary-600' : 'text-gray-500'}`} />
                      <span>{tab.nameKey ? t(tab.nameKey) : tab.name}</span>
                    </div>
                    {tab.badge && (
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-secondary-500 text-white">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {renderTabContent()}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Action</h3>
            <p className="text-sm text-gray-600 mb-6">{confirmAction.message}</p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                {t('cms.common.cancel')}
              </button>
              <button
                onClick={() => {
                  confirmAction.onConfirm();
                  setConfirmAction(null);
                }}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;